<template>
  <div class="site-header-container">
    <header
      ref="siteHeader"
      class="site-header"
      :class="[
        $class(headerState),
      ]"
      data-sign-post-priority="1"
      :js-sign-post="$string('accessibility.sign_posts.site_header')"
    >
      <pre-header-banner
        v-if="showPreHeaderBanner"
        :background-color="preHeaderBannerBackground"
        :banners="preHeaderBannerContent"
        :text-color-theme="preHeaderBannerTextColour"
      />
      <div
        ref="masthead"
        class="site-header__masthead"
        @mouseover="loadComponent('menu-shelf')"
        @touchstart="loadComponent('menu-shelf')"
      >
        <div
          class="site-header__container"
          js-site-header="container"
        >
          <!-- Header logo -->
          <a
            class="site-header__logo"
            :href="getLocalisedUrl($variable('routes.root'))"
            :aria-label="$string('general.site_header.return_home')"
          >
            <icon-logo-word />
          </a>

          <div
            class="site-header__menu-nav"
            @mouseleave="handleMenuMouseLeave"
          >
            <nav
              v-if="showNavigation"
              class="
                site-header__navigation
                site-header__navigation--primary
              "
              :aria-label="$string('accessibility.sign_posts.site_header')"
            >
              <ul class="site-header__navigation-list">
                <li
                  v-for="(tier1) in navigation.slice(0, linkItemCount)"
                  :key="tier1.handle"
                  class="site-header__navigation-item"
                >
                  <a
                    class="site-header__navigation-link text-body-2xs"
                    :href="getLocalisedUrl(tier1.url)"
                    @mouseover="handleLinkHover($event, tier1)"
                    v-text="tier1.title"
                  />
                </li>

                <li
                  v-if="isCustomer && (eagleClubMemberLink.enabled && eagleClubMemberLink.url)"
                  class="eagle-club-member-link site-header__navigation-item"
                >
                  <a
                    class="site-header__navigation-link text-body-2xs"
                    :href="getLocalisedUrl(eagleClubMemberLink.url)"
                  >
                    <span v-text="eagleClubMemberLink.label" />
                    <icon-eagle />
                  </a>
                </li>
              </ul>
            </nav>

            <overlay
              v-if="showNavigation"
              key="menu-shelf"
              block-class="menu-shelf-overlay"
              direction="down"
              namespace="menu-shelf"
              :show-close="false"
              type="shelf"
            >
              <template #body="{ isActive }">
                <menu-shelf
                  v-if="getComponentVisible('menu-shelf')"
                  v-cloak
                  :key="drawerKey"
                  ref="menu-shelf"
                  :drawer-lower-image="drawerLowerImage"
                  :drawer-lower-links="drawerLowerLinks"
                  :add-sale-highlight="addSaleHighlight"
                  :is-active="isActive"
                  :images="images"
                  :navigation="navigation"
                  :drawer-navigation="drawerNavigation"
                  :active-menu="activeMenu"
                  :show-drawer-account="showDrawerAccount"
                  namespace="menu-shelf"
                  :eagle-club-non-member-link="eagleClubNonMemberLink"
                  :eagle-club-member-link="eagleClubMemberLink"
                  @clear-active-menu="() => activeMenu = null"
                />
              </template>
            </overlay>

            <header-search
              v-if="showSearch"
              class="site-header__menu--search site-header__navigation-link"
              view="toggle-modal"
            />
          </div>

          <nav
            class="
              site-header__navigation
              site-header__navigation--additional
            "
            :aria-label="$string('accessibility.sign_posts.site_header_additional')"
          >
            <ul class="site-header__navigation-list">
              <transition name="fade-right">
                <li
                  v-show="!searchModalActive"
                  class="site-header__navigation-item site-header__navigation-item--desktop"
                >
                  <market-switcher-trigger class="site-header__navigation-link" />
                </li>
              </transition>

              <li
                v-if="!isCustomer && (eagleClubNonMemberLink.enabled && eagleClubNonMemberLink.url)"
                class="
                  site-header__navigation-item
                  site-header__navigation-item--desktop
                  eagle-club-non-member-link
                "
              >
                <a
                  class="site-header__navigation-link text-body-2xs"
                  :href="getLocalisedUrl(eagleClubNonMemberLink.url)"
                  :aria-label="eagleClubNonMemberLink.label"
                >
                  <span
                    class="text-body-2xs"
                    v-text="eagleClubNonMemberLink.label"
                  />
                  <icon-eagle />
                </a>
              </li>

              <li
                v-if="showSearch"
                class="site-header__navigation-item site-header__navigation-item--desktop"
              >
                <header-search class="site-header__navigation-link" />
              </li>

              <li
                v-if="showAccount"
                class="site-header__navigation-item site-header__navigation-item--desktop"
              >
                <a
                  class="site-header__navigation-link"
                  :href="getLocalisedUrl($variable('routes.account.url'))"
                  :aria-label="createAriaLabel('account')"
                >
                  <icon-account />
                </a>
              </li>

              <li
                v-if="showCart"
                class="site-header__navigation-item site-header__navigation-item-cart"
              >
                <a
                  class="site-header__navigation-link"
                  :href="getLocalisedUrl($variable('routes.cart.url'))"
                  :aria-label="createAriaLabel('cart')"
                  @click="handleCartToggle"
                  @mousedown="loadComponent('cart-drawer')"
                  @touchstart="loadComponent('cart-drawer')"
                >
                  <icon-bag />
                </a>
                <transition name="fade-down">
                  <span
                    v-if="cart.item_count > 0"
                    aria-hidden="true"
                    class="site-header__cart-badge"
                    v-text="cart.item_count"
                  />
                </transition>
              </li>

              <li
                v-if="showNavigation"
                class="site-header__navigation-item site-header__menu"
              >
                <button
                  class="site-header__menu"
                  :aria-label="$string('general.site_header.menu')"
                  @click="toggleOverlay({
                    component: 'menu-shelf',
                    ignoreDismissed: true,
                    namespace: 'menu-shelf',
                  })"
                >
                  <icon-menu />
                  <icon-close />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <secondary-header
        v-if="secondaryLinks.length > 0"
        :header-height="height.total"
        :links="secondaryLinks"
      />
    </header>
  </div>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import PreHeaderBanner from './pre-header-banner'
import MarketSwitcherTrigger from '~async/market-switcher-trigger/market-switcher-trigger'
import SecondaryHeader from '~async/secondary-header/secondary-header'
import HeaderSearch from '~async/header-search/header-search'
import MenuShelf from '~async/menu-shelf/menu-shelf'
import IconAccount from '~icons/misc/account.svg'
import IconBag from '~icons/misc/bag.svg'
import IconLogoWord from '~icons/misc/logo-word.svg'
import IconMenu from '~icons/misc/menu.svg'
import IconClose from '~icons/misc/close.svg'
import IconEagle from '~icons/misc/eagle.svg'
import { getLocalisedRoute } from '~/helpers/routes'

export default {
  name: 'SiteHeader',

  components: {
    IconClose,
    IconAccount,
    IconBag,
    IconLogoWord,
    IconMenu,
    PreHeaderBanner,
    HeaderSearch,
    SecondaryHeader,
    MarketSwitcherTrigger,
    MenuShelf,
    IconEagle,
  },

  props: {
    addSaleHighlight: {
      type: Boolean,
      default: false,
    },
    drawerLowerImage: {
      type: String,
      default: '',
    },
    drawerLowerLinks: {
      type: Array,
      default: () => [],
    },
    images: {
      type: Array,
      default: () => ([]),
    },
    drawerNavigation: {
      type: Array,
      default: () => [],
    },
    eagleClubNonMemberLink: {
      type: Object,
      default: () => ({}),
    },
    eagleClubMemberLink: {
      type: Object,
      default: () => ({}),
    },
    navigation: {
      type: Array,
      default: () => [],
    },
    preHeaderBannerBackground: {
      type: String,
      default: '',
    },
    preHeaderBannerTextColour: {
      type: String,
      default: '',
    },
    preHeaderBannerContent: {
      type: Array,
      default: () => [],
    },
    showAccount: {
      type: Boolean,
      default: false,
    },
    showCart: {
      type: Boolean,
      default: false,
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    showNavigation: {
      type: Boolean,
      default: false,
    },
    showDrawerAccount: {
      type: Boolean,
      default: true,
    },
    showPreHeaderBanner: {
      type: Boolean,
      default: true,
    },
    linkItemCount: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      activeMenu: null,
      drawerKey: 0,
      height: {
        masthead: 0,
        total: 0,
        visible: 0,
      },
      searchModalActive: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      breakpoint: (state) => state.index.screen.breakpoint,
      cart: (state) => state.cart.response,
      cartHistory: (state) => state.cart.history,
      headerState: (state) => state.index.scroll.header,
      loadedStylesheets: (state) => state.index.stylesheets,
      settings: (state) => state.index.settings,
      secondaryLinks: (state) => state.header.secondaryLinks,
      isMobile: (state) => state.index.screen.mobile,
      isCustomer: (state) => state.index.isCustomer,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),
  },

  watch: {

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
        return
      }

      this.$refs.siteHeader.ontransitionend = (args) => {
        if (!args || args.propertyName !== 'transform') {
          return
        }

        this.setHeights()
      }
    },

    /**
     * Watch total height to update CSS variables.
     */
    'height.total': function() {
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.height.total}px`,
      )
    },

    /**
     * Watch visible height to update CSS variable.
     */
    'height.visible': function() {
      document.documentElement.style.setProperty(
        '--header-visible-height',
        `${this.height.visible}px`,
      )
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    loadedStylesheets() {
      if (!this.loadedStylesheets.includes('layout.theme.css')) {
        return
      }

      /**
       * Delay set height to wait for CSS to be parsed.
       */
      setTimeout(() => {
        this.setHeights()
      }, this.$timing('quick'))
    },

    /**
     * Watch active overlay to reset current link.
     */
    async getActiveOverlay() {
      await this.$nextTick()

      if (this.getActiveOverlay?.namespace === 'search-modal') {
        this.searchModalActive = true
        return
      }

      this.searchModalActive = false
    },
  },

  async mounted() {
    await this.$nextTick()
    this.setHeights(true)

    cnvs.EventBus.listen('cnvs:resize', () => {
      this.setHeights(true)
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadComponent: 'components/load',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Helper to consyruct an aria-label
     * @param {String} link- String identifying the icon/button.
     * @returns {String} - Constructed aria-label string.
     */
    createAriaLabel(link) {
      return `${this.$string('accessibility.open')} ${link} ${this.$string('general.site_header.icon_button')}`
    },

    getLocalisedUrl(url) {
      return getLocalisedRoute(url)
    },

    /**
     * Set heights of header.
     * - Height.total is used for body padding.
     * - Height.visible is used for overlays etc.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted) {
      if (mounted) {
        this.height.total = this.$refs.siteHeader.offsetHeight
      }

      this.height.visible = (this.headerState === 'closed')
        ? 0
        : this.$refs.masthead.offsetHeight
    },

    /**
     * Handle link click.
     * @param {Object|Boolean} link - Clicked link object, or false.
     */
    handleLinkHover(event, link) {
      if (link.links.length < 1) {
        this.closeOverlay('menu-shelf')
        return
      }

      event.preventDefault()
      this.activeMenu = link.title

      this.openOverlay({
        component: 'menu-shelf',
        ignoreDismissed: true,
        namespace: 'menu-shelf',
      })
    },

    /**
     * Handle menu mouse leave.
     */
    handleMenuMouseLeave() {
      if (this.isMobile) {
        return
      }

      this.closeOverlay('menu-shelf')
    },

    /**
     * Handle cart toggle.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      event.preventDefault()

      this.openOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },
  },
}
</script>
